import { useState, useEffect, useCallback } from 'react';
const PWA_INSTALLED_KEY = 'dwinity_pwa_installed';
let sharedDeferredPrompt = null;
let globalListenerAdded = false;
function isStandalone() {
    if (window.matchMedia('(display-mode: standalone)').matches)
        return true;
    if (navigator.standalone === true)
        return true;
    if (document.referrer.includes('android-app://'))
        return true;
    return false;
}
function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function checkPreviouslyInstalled() {
    try {
        return localStorage.getItem(PWA_INSTALLED_KEY) === 'true';
    }
    catch {
        return false;
    }
}
function markAsInstalled() {
    try {
        localStorage.setItem(PWA_INSTALLED_KEY, 'true');
    }
    catch { }
}
if (!globalListenerAdded && typeof window !== 'undefined') {
    globalListenerAdded = true;
    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        sharedDeferredPrompt = e;
        console.log('[PWA] beforeinstallprompt captured globally');
    });
    window.addEventListener('appinstalled', () => {
        sharedDeferredPrompt = null;
        markAsInstalled();
        console.log('[PWA] appinstalled event — marked as installed');
    });
}
export function usePwaInstall() {
    const [canInstall, setCanInstall] = useState(false);
    const [showIOSGuide, setShowIOSGuide] = useState(false);
    const [isAlreadyInstalled, setIsAlreadyInstalled] = useState(false);
    const [installDismissed, setInstallDismissed] = useState(false);
    useEffect(() => {
        if (isStandalone()) {
            markAsInstalled();
            setIsAlreadyInstalled(true);
            return;
        }
        if (checkPreviouslyInstalled()) {
            setIsAlreadyInstalled(true);
            return;
        }
        if (isIOS()) {
            if (sessionStorage.getItem('pwa_ios_guide_dismissed') !== 'true') {
                setShowIOSGuide(true);
                setCanInstall(true);
            }
            return;
        }
        if (sharedDeferredPrompt) {
            setCanInstall(true);
        }
        const handler = () => {
            setCanInstall(true);
        };
        window.addEventListener('beforeinstallprompt', handler);
        const installedHandler = () => {
            setCanInstall(false);
            setIsAlreadyInstalled(true);
            setInstallDismissed(false);
        };
        window.addEventListener('appinstalled', installedHandler);
        return () => {
            window.removeEventListener('beforeinstallprompt', handler);
            window.removeEventListener('appinstalled', installedHandler);
        };
    }, []);
    const install = useCallback(async () => {
        if (showIOSGuide)
            return false;
        const prompt = sharedDeferredPrompt;
        if (!prompt) {
            console.warn('[PWA] No deferred prompt available');
            setInstallDismissed(true);
            setCanInstall(false);
            return false;
        }
        try {
            console.log('[PWA] Calling prompt.prompt()');
            await prompt.prompt();
            const result = await prompt.userChoice;
            console.log('[PWA] User choice:', result.outcome);
            sharedDeferredPrompt = null;
            if (result.outcome === 'accepted') {
                setCanInstall(false);
                setIsAlreadyInstalled(true);
                setInstallDismissed(false);
                markAsInstalled();
                return true;
            }
            else {
                setCanInstall(false);
                setInstallDismissed(true);
                return false;
            }
        }
        catch (err) {
            console.error('[PWA] Install prompt error:', err);
            setCanInstall(false);
            setInstallDismissed(true);
            return false;
        }
    }, [showIOSGuide]);
    const dismissIOSGuide = useCallback(() => {
        setShowIOSGuide(false);
        setCanInstall(false);
        sessionStorage.setItem('pwa_ios_guide_dismissed', 'true');
    }, []);
    return {
        canInstall,
        isIOS: isIOS() && !isStandalone(),
        showIOSGuide,
        isAlreadyInstalled,
        installDismissed,
        install,
        dismissIOSGuide,
        isStandalone: isStandalone(),
    };
}
